// eslint-disable-next-line max-classes-per-file
import * as SurveyCore from "survey-core";
import { ReactQuestionFactory, SurveyQuestionElementBase } from "survey-react-ui";
import React from "react";
import DatePicker from "react-datepicker";
import { format, isValid, parse } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import SetContactMaxLengthAndValue from "./common/SetContactMaxLengthAndValue";
import SetLeadMaxLengthAndValue from "./common/SetLeadMaxLengthAndValue";
import i18next from "../../common/localization/i18n";
import FormStateParams from "../interfaces/FormStateParams";
import { SupportedCRM } from "../../common/enums/SupportedCRM";
import SetMapperAttributes from "../../common/helpers/SetMapperAttributes";
import { EntityType } from "../../common/enums/EntityType";

const CUSTOM_TYPE = "datetimepicker";

export class QuestionDateTimePickerModel extends SurveyCore.Question {
  // eslint-disable-next-line class-methods-use-this
  getType() {
    return CUSTOM_TYPE;
  }

  get dateTimePickerType() {
    return this.getPropertyValue("dateTimePickerType");
  }

  set dateTimePickerType(val) {
    this.setPropertyValue("dateTimePickerType", val);
  }

  get dateFormat() {
    return this.getPropertyValue("dateFormat");
  }

  set dateFormat(val) {
    if (this.getPropertyValue("dateFormat") !== val && this.value) {
      this.setPropertyValue("defaultValue", null);
      this.setPropertyValue("value", null);
    }
    this.setPropertyValue("dateFormat", val);
  }

  get timeFormat() {
    return this.getPropertyValue("timeFormat");
  }

  set timeFormat(val) {
    this.setPropertyValue("timeFormat", val);
  }
}

// A class that renders questions of the new type in the UI
export class SurveyQuestionDateTimePicker extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);

    this.state = { value: this.question.value };
  }

  get question() {
    return this.questionBase;
  }

  get value() {
    return this.question.value;
  }

  get dateFormat() {
    return this.question.dateFormat;
  }

  get type() {
    return this.question.dateTimePickerType;
  }

  get pickerDateTimeValue() {
    if (!this.question.value) {
      return null;
    }

    const parsedDate = parse(this.question.value, this.getDateTimeFormat(), new Date());

    this.question.pickerDateTimeValue = isValid(parsedDate) ? parsedDate : null;
    return this.question.pickerDateTimeValue;
  }

  // Support the read-only and design modes
  get style() {
    return this.question.getPropertyValue("readOnly") || this.question.isDesignMode
      ? { pointerEvents: "none" }
      : undefined;
  }

  getDateTimeFormat = () => {
    let dateFormat = `${this.question.dateFormat}`;
    if (this.question.dateTimePickerType === "DateTime") {
      dateFormat += ` ${this.question.timeFormat}`;
    }

    return dateFormat;
  };

  handleDateChange = (date: Date) => {
    this.question.pickerDateTimeValue = date;
    this.question.value = format(new Date(date), this.getDateTimeFormat());
  };

  // eslint-disable-next-line class-methods-use-this,@typescript-eslint/no-unused-vars
  renderDate(type: any) {
    if (type === "Date") {
      return (
        <DatePicker
          selected={this.pickerDateTimeValue}
          dateFormat={this.dateFormat}
          yearDropdownItemNumber={105}
          scrollableYearDropdown
          showMonthDropdown
          showYearDropdown
          onChange={(date: Date) => {
            this.handleDateChange(date);
          }}
        />
      );
    }
    return (
      <DatePicker
        selected={this.pickerDateTimeValue}
        dateFormat={`${this.question.dateFormat} ${this.question.timeFormat}`}
        showTimeSelect
        yearDropdownItemNumber={105}
        scrollableYearDropdown
        showMonthDropdown
        showYearDropdown
        timeFormat={this.question.timeFormat}
        onChange={(date: Date) => {
          this.handleDateChange(date);
        }}
      />
    );
  }

  renderElement() {
    // @ts-ignore
    return <div style={this.style}>{this.renderDate(this.type)}</div>;
  }
}

export function DateTimeControl(formStateParams: FormStateParams) {
  SurveyCore.Serializer.addClass(
    "datetimepicker",
    [
      {
        name: "dateTimePickerType",
        displayName: i18next.t("NEW_FORM_EDITOR.DATE_TIME.PICKER_TYPE"),
        default: "Slider",
        choices: [
          { value: "Date", text: i18next.t("NEW_FORM_EDITOR.DATE_TIME.DATE") },
          { value: "DateTime", text: i18next.t("NEW_FORM_EDITOR.DATE_TIME.DATE_TIME") },
        ],
        category: "general",
        visibleIndex: 2, // After the Name and Title
      },
      {
        name: "dateFormat",
        displayName: i18next.t("NEW_FORM_EDITOR.DATE_TIME.DATE_FORMAT"),
        choices: [
          { value: "dd/MM/yyyy", text: "dd/mm/yyyy" },
          { value: "MM/dd/yyyy", text: "mm/dd/yyyy" },
          { value: "yyyy/MM/dd", text: "yyyy/mm/dd" },
        ],
        category: "general",
        visibleIndex: 3,
      },
      {
        name: "timeFormat",
        displayName: i18next.t("NEW_FORM_EDITOR.DATE_TIME.TIME_FORMAT"),
        dependsOn: ["dateTimePickerType"],
        choices: [
          { value: "hh:mm aa", text: "12h" },
          { value: "HH:mm", text: "24h" },
        ],
        visibleIf: (obj: any) => {
          return obj.dateTimePickerType === "DateTime";
        },
        default: "hh:mm aa",
        category: "general",
        visibleIndex: 4,
      },
    ],
    () => {
      return new QuestionDateTimePickerModel("");
    },
    "question",
  );

  SurveyCore.ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name) => {
    return new QuestionDateTimePickerModel(name);
  });

  ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
    return React.createElement(SurveyQuestionDateTimePicker, props);
  });

  SurveyCore.Serializer.addProperty("datetimepicker", {
    name: "crmMaxLength",
    type: "number",
    default: 1,
    isSerializable: true,
    visible: false,
  });
  SurveyCore.Serializer.addProperty("datetimepicker", {
    name: "crmContactMaxLength",
    type: "number",
    default: 1,
    isSerializable: true,
    visible: false,
  });
  SurveyCore.Serializer.addProperty("datetimepicker", {
    name: "crmLeadMaxLength",
    type: "number",
    default: 1,
    isSerializable: true,
    visible: false,
  });

  SurveyCore.Serializer.addProperty("datetimepicker", {
    name: "contactMap",
    displayName: i18next.t("NEW_FORM_EDITOR.COMPONENT_COMMON.CONTACT_MAP"),
    isRequired: false,
    type: "dropdown",
    dependsOn: ["dateTimePickerType"],
    isSerializable: true,
    category: "toolboxMapperCategory",
    onPropertyEditorUpdate: (obj: any, editor: any) => {
      editor.allowClear = true;
    },
    onSetValue: (surveyElement: any, value: any) => {
      if (surveyElement.dateTimePickerType === "Date") {
        SetContactMaxLengthAndValue(surveyElement, value, "contact", "date");
      }
      if (surveyElement.dateTimePickerType === "DateTime") {
        SetContactMaxLengthAndValue(surveyElement, value, "contact", "dateTime");
      }
    },
    choices: (obj: any, choicesCallback: any) => {
      if (!choicesCallback) return;
      if (obj.dateTimePickerType === "Date") {
        SetMapperAttributes("date", choicesCallback, formStateParams, EntityType.Contact, obj);
      }
      if (obj.dateTimePickerType === "DateTime") {
        SetMapperAttributes("dateTime", choicesCallback, formStateParams, EntityType.Contact, obj);
      }
    },
  });

  if (formStateParams.supportedCRM === SupportedCRM.MSCRM) {
    SurveyCore.Serializer.addProperty("datetimepicker", {
      name: "leadMap",
      displayName: i18next.t("NEW_FORM_EDITOR.COMPONENT_COMMON.LEAD_MAP"),
      isRequired: false,
      category: "toolboxMapperCategory",
      type: "dropdown",
      dependsOn: ["dateTimePickerType"],
      isSerializable: true,
      onPropertyEditorUpdate: (obj: any, editor: any) => {
        editor.allowClear = true;
      },
      onSetValue: (surveyElement: any, value: any) => {
        if (surveyElement.dateTimePickerType === "Date") {
          SetLeadMaxLengthAndValue(surveyElement, value, "lead", "date");
        }
        if (surveyElement.dateTimePickerType === "DateTime") {
          SetLeadMaxLengthAndValue(surveyElement, value, "lead", "dateTime");
        }
      },
      choices: (obj: any, choicesCallback: any) => {
        if (!choicesCallback) return;
        if (obj.dateTimePickerType === "Date") {
          SetMapperAttributes("date", choicesCallback, formStateParams, EntityType.Lead, obj);
        }
        if (obj.dateTimePickerType === "DateTime") {
          SetMapperAttributes("dateTime", choicesCallback, formStateParams, EntityType.Lead, obj);
        }
      },
    });
  }

  SurveyCore.Serializer.addProperty("datetimepicker", {
    name: "overrideInCrmMap",
    displayName: i18next.t("NEW_FORM_EDITOR.COMPONENT_COMMON.OVERRIDE_IN_CRM"),
    isRequired: false,
    category: "toolboxMapperCategory",
    type: "boolean",
    categoryIndex: 8,
  });
}
